import Vue from 'vue'
import axios, { AxiosError } from 'axios'
import store from '@/store'
import ServerError from '@/utils/server_error'

import userStore from '@/modules/auth/auth.store'
import layoutStore from '@/modules/shared/layout.store'
import { getModule } from 'vuex-module-decorators'
import { setTimeOffset } from '@/utils/time'
import i18n from '@/lang'
import { isElectron } from '@/utils/electron'
import { ResponseStatus } from '@/api'
import { checkForUpdates, isDownloadingUpdates } from '@/utils/autoUpdate'
import getAPIEndpoint from '@/utils/endpoint'

let headers = {
  'X-Client-Type': isElectron() ? 'DesktopClient' : 'WebApp',
  'X-Client-Version': process.env.PACKAGE_VERSION || '0.0.0'
}

const service = axios.create({
  baseURL: getAPIEndpoint(),
  timeout: 30 * 1000,
  headers
})

service.interceptors.request.use(
  config => {
    const userModule = getModule(userStore, store)
    config.headers['Accept-Language'] = i18n.locale

    let token = userModule.getAuthToken

    if (!isElectron() && !token && !userModule.oidcAccessToken) {
      token = localStorage.getItem('access_token')
    }

    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    return Promise.resolve(config)
  },
  error => {
    return Promise.reject(error)
  }
)

// 400 client update interceptor
service.interceptors.response.use(
  response => {
    return response
  },
  async (error: AxiosError) => {
    // Ignore any non 400 bad request
    if (
      !error.response ||
      error.response!.status !== 400 ||
      !error.response!.data ||
      !error.response!.data.status
    ) {
      throw error
    }

    if (error.response!.data!.status === ResponseStatus.UpdateRequired) {
      // check if update is in progress
      if (await isDownloadingUpdates()) {
        return
      }

      const layoutModule = getModule(layoutStore, store)
      layoutModule.setUpdateDownloadProgress({
        bytesPerSecond: 0,
        percent: 0,
        total: 0,
        transferred: 0
      })

      await checkForUpdates()
    }

    throw error
  }
)

// 401 response token refresh interceptor
service.interceptors.response.use(
  response => {
    return response
  },
  async (error: AxiosError) => {
    const userModule = getModule(userStore, store)
    // Do not refresh token if we haven't login
    if (!userModule.hasLoggedIn) {
      throw error
    }

    // Ignore any non 401 unauthorized response
    if (!error.response || error.response!.status !== 401) {
      throw error
    }
    // Remove session info if refresh token failed
    if (error.config.url === '/auth/v1/refreshToken') {
      Vue.prototype.$toast.error(
        'Your session has been expired.\nPlease re-try login.'
      )

      throw error
    }

    // Try to refresh token
    await userModule.requestTokenRefresh()

    // Attempt to re-fire the request
    const token = userModule.getAuthToken

    if (token) {
      // New request with new token
      const config = error.config
      config.headers['Authorization'] = `Bearer ${token}`

      try {
        return axios.request(config)
      } catch (error) {
        throw error
      }
    } else {
      throw error
    }
  }
)

service.interceptors.response.use(
  response => {
    return response
  },
  async (error: AxiosError) => {
    // Ignore any non 451 unavailable for legal reason request
    if (!error.response || error.response!.status !== 451) {
      throw error
    }
    window.ipcRenderer.send('quit-unofficial-copy')

    throw error
  }
)

// Response code handling : Response Interceptor
service.interceptors.response.use(
  response => {
    if (response.data.serverTime) {
      setTimeOffset(new Date(response.data.serverTime * 1000))
    }

    return response
  },
  async error => {
    if (error.isAxiosError) {
      return Promise.reject(new ServerError(error))
    } else {
      return Promise.reject(error)
    }
  }
)

export default service
